import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type JSX } from 'react';

import styles from './UsabilityPanelPage.module.scss';
import { getUsabilityHeatmap, getUsabilityMetrics } from '../api/usability.graphql';
import { EmptyState } from '../components/EmptyState';
import { HeatmapOverlay } from '../components/HeatmapOverlay';
import { MetricsSummary } from '../components/MetricsSummary';
import { UsabilityFilters, type UsabilityFiltersValue } from '../components/UsabilityFilters';

function todayIsoDate(daysAgo = 0): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

function defaultFilters(): UsabilityFiltersValue {
  return { tenantId: '', dateFrom: todayIsoDate(30), dateTo: todayIsoDate(), page: '', module: '' };
}

export function UsabilityPanelPage(): JSX.Element {
  const [filters, setFilters] = useState<UsabilityFiltersValue>(defaultFilters);

  const queryFilter = useMemo(
    () => ({
      tenantId: filters.tenantId,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      page: filters.page || undefined,
      module: filters.module || undefined,
    }),
    [filters],
  );

  const heatmapQ = useQuery({
    queryKey: ['usability-heatmap', queryFilter],
    queryFn: () => getUsabilityHeatmap(queryFilter),
    enabled: Boolean(filters.tenantId),
  });

  const metricsQ = useQuery({
    queryKey: ['usability-metrics', queryFilter],
    queryFn: () => getUsabilityMetrics(queryFilter),
    enabled: Boolean(filters.tenantId),
  });

  return (
    <div className={styles.page}>
      <UsabilityFilters value={filters} onChange={setFilters} />

      {!filters.tenantId && <EmptyState message="Selecciona un tenant para ver sus métricas de usabilidad" />}

      {filters.tenantId && (
        <>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Métricas de uso</h2>
            {metricsQ.isLoading ? (
              <EmptyState message="Cargando…" />
            ) : (
              <MetricsSummary metrics={metricsQ.data?.edges.map((e) => e.node) ?? []} />
            )}
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Mapa de calor</h2>
            {heatmapQ.isLoading ? (
              <EmptyState message="Cargando…" />
            ) : (
              <HeatmapOverlay points={heatmapQ.data ?? []} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
